import {
  Box,
  Button,
  Flex,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdImage, MdOndemandVideo } from "react-icons/md";
import YouTube from "react-youtube";
import PdfPreviewModal from "../../../components/student/PdfPreviewModal";
import { studentPalette } from "../../../constants/studentPalette";
import { extractYouTubeVideoId } from "../../../utils/youtubeVideoId";
import {
  isPdfAsset,
  isValidQuestionAsset,
  questionAssetUrl,
  questionPdfUrl,
} from "./exam-answer-media";

const BASE_URL = "https://edutopiaonline.com/api";
const MODAL = {
  HINT: "hint",
  VIDEO: "video",
  CAMBRIDGE: "cambridge",
};

const ACADEMIC = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
};

const youtubeOpts = {
  height: 360,
  width: 640,
  playerVars: {
    autoplay: 0,
    rel: 0,
  },
};

function isValidHtmlContent(value) {
  if (!value || value === "null" || value === "undefined") return false;
  return String(value).replace(/<[^>]*>/g, "").trim().length > 0;
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    if (value == null) continue;
    const s = String(value).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return "";
}

function getQuestionVideoRaw(question) {
  const nested = question?.question;
  const nestedUrl =
    nested && typeof nested === "object" && !Array.isArray(nested) ? nested.url : "";
  return firstNonEmptyString(
    question?.url,
    question?.video_url,
    question?.youtube_url,
    question?.video,
    nestedUrl
  );
}

function iframeSrc(html) {
  if (!html || !/<iframe/i.test(html)) return "";
  const match = html.match(/\ssrc=["']([^"']+)["']/i);
  return match?.[1]?.trim() || "";
}

function resolveVideoId(raw) {
  if (!raw) return null;
  return (
    extractYouTubeVideoId(raw) ||
    extractYouTubeVideoId(iframeSrc(raw)) ||
    null
  );
}

function isHomeworkClassifiedType(examType, question) {
  const nested = question?.exam;
  const candidates = [examType, question?.exam_type, nested?.type, nested?.exam_type];
  return candidates.some((t) => t != null && t !== "" && Number(t) === 2);
}

const QuestionResourceActions = ({
  question,
  variant = "review",
  examType,
  design = "default",
  showAllButtons = false,
}) => {
  const [activeModal, setActiveModal] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const isAcademic = design === "academic";
  const isMissed = design === "missed";
  const isSolve = variant === "solve";

  const hasHint = isValidQuestionAsset(question?.model_answer_image);
  const rawVideo = useMemo(() => getQuestionVideoRaw(question), [question]);
  const videoId = useMemo(() => resolveVideoId(rawVideo), [rawVideo]);
  const hasIframeHint = /<iframe/i.test(rawVideo);
  const hasVideo = Boolean(videoId || hasIframeHint || /^https?:\/\//i.test(rawVideo));
  const cambridgeAnswer =
    question?.cambridge_answer ?? question?.cambrige_answer;
  const hasCambridgeAnswer = isValidHtmlContent(cambridgeAnswer);
  const hasCambridgeImage = isValidQuestionAsset(question?.cambridge_image);
  const hasCambridge = hasCambridgeAnswer || hasCambridgeImage;

  const isType2 = isHomeworkClassifiedType(examType, question);
  const typeKnown = [examType, question?.exam_type, question?.exam?.type].some(
    (t) => t != null && t !== ""
  );
  const showHint = !isSolve && hasHint;
  const showVideo = isSolve ? (isType2 || !typeKnown) && hasVideo : hasVideo;
  const showCambridge = !isSolve && hasCambridge;

  const renderVideo = showAllButtons || showVideo;
  const renderHint = showAllButtons || showHint;
  const renderCambridge = showAllButtons || showCambridge;

  useEffect(() => {
    setActiveModal(null);
    setPdfPreview(null);
  }, [question?.id]);

  if (!renderVideo && !renderHint && !renderCambridge) return null;

  const buttonSx = isAcademic
    ? {
        bg: ACADEMIC.primary,
        color: "white",
        px: { base: 5, md: 8 },
        py: 5,
        borderRadius: "xl",
        fontWeight: "800",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        fontSize: "xs",
        _hover: { bg: ACADEMIC.accent },
        boxShadow: "md",
      }
    : isMissed
      ? {
          size: "sm",
          bg: ACADEMIC.primary,
          color: studentPalette.cream,
          fontWeight: "700",
          borderRadius: "md",
          _hover: { bg: ACADEMIC.accent },
          _disabled: { opacity: 0.45, cursor: "not-allowed", _hover: { bg: ACADEMIC.primary } },
        }
      : {
          bg: "main_1",
          color: "white",
          size: "sm",
        };

  const closeModal = () => setActiveModal(null);

  const openHint = () => {
    if (!hasHint) return;
    if (isPdfAsset(question?.model_answer_image)) {
      setPdfPreview({
        url: questionPdfUrl(question.model_answer_image),
        title: "Handwritten Answer",
      });
      return;
    }
    setActiveModal(MODAL.HINT);
  };

  const openCambridge = () => {
    if (!hasCambridge) return;
    if (!hasCambridgeAnswer && isPdfAsset(question?.cambridge_image)) {
      setPdfPreview({
        url: questionPdfUrl(question.cambridge_image),
        title: "Mark Scheme Answer",
      });
      return;
    }
    setActiveModal(MODAL.CAMBRIDGE);
  };

  const renderButton = (visible, enabled, icon, label, onClick, ariaLabel) => {
    if (!visible) return null;

    const hasLabel = Boolean(String(label || "").trim());

    return (
      <Button
        {...buttonSx}
        leftIcon={hasLabel ? icon : undefined}
        isDisabled={showAllButtons && !enabled}
        onClick={() => enabled && onClick?.()}
        aria-label={!hasLabel ? ariaLabel : undefined}
        minW={hasLabel ? undefined : "40px"}
        px={hasLabel ? buttonSx.px : 2}
        justifyContent="center"
      >
        {hasLabel ? label : icon}
      </Button>
    );
  };

  return (
    <>
      <Flex
        w="100%"
        justify={isMissed ? "flex-start" : "flex-end"}
        gap={3}
        flexWrap="wrap"
        mb={4}
      >
        {renderButton(
          renderVideo,
          showVideo,
          <MdOndemandVideo size={18} />,
          isSolve ? "Video hint" : "",
          () => setActiveModal(MODAL.VIDEO),
          "Video Hint"
        )}
        {renderButton(
          renderHint,
          showHint,
          <Text as="span" fontSize="18px" lineHeight="1" aria-hidden>
            📝
          </Text>,
          "",
          openHint,
          "Handwritten Answer"
        )}
        {renderButton(
          renderCambridge,
          showCambridge,
          <MdImage size={18} />,
          "MS",
          openCambridge
        )}
      </Flex>

      <Modal isCentered isOpen={activeModal === MODAL.HINT} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Handwritten Answer
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Image
              src={questionAssetUrl(question?.model_answer_image, BASE_URL)}
              alt="Handwritten answer"
              w="100%"
              maxW={{ base: "100%", md: "480px" }}
              maxH="400px"
              objectFit="contain"
              borderRadius="md"
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isCentered isOpen={activeModal === MODAL.VIDEO} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "3xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Video Hint
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            <Box
              w="100%"
              maxW="640px"
              sx={{
                "& iframe": {
                  display: "block",
                  maxWidth: "100%",
                  width: "100%",
                },
              }}
            >
              {videoId ? (
                <YouTube
                  videoId={videoId}
                  opts={youtubeOpts}
                  onReady={(event) => event.target.pauseVideo()}
                />
              ) : hasIframeHint ? (
                <Box dangerouslySetInnerHTML={{ __html: rawVideo }} />
              ) : (
                <Link
                  href={rawVideo}
                  isExternal
                  color={ACADEMIC.primary}
                  fontWeight="700"
                  wordBreak="break-all"
                >
                  {rawVideo}
                </Link>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isCentered isOpen={activeModal === MODAL.CAMBRIDGE} onClose={closeModal} motionPreset="slideInBottom">
        <ModalOverlay />
        <ModalContent maxW={{ base: "calc(100vw - 24px)", md: "xl" }}>
          <ModalHeader fontSize="md" fontWeight="800">
            Mark Scheme Answer
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} display="flex" justifyContent="center">
            {hasCambridgeAnswer ? (
              <Box
                w="100%"
                maxW={{ base: "100%", md: "640px" }}
                lineHeight="1.65"
                sx={{
                  "html[data-theme='dark'] &": { color: "var(--text-primary)" },
                  "html[data-theme='dark'] & p, html[data-theme='dark'] & span, html[data-theme='dark'] & div, html[data-theme='dark'] & li":
                    { color: "inherit" },
                }}
                dangerouslySetInnerHTML={{ __html: cambridgeAnswer }}
              />
            ) : (
              <Image
                src={questionAssetUrl(question?.cambridge_image, BASE_URL)}
                alt="Mark scheme answer"
                w="100%"
                maxW={{ base: "100%", md: "480px" }}
                maxH="400px"
                objectFit="contain"
                borderRadius="md"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <PdfPreviewModal
        isOpen={Boolean(pdfPreview?.url)}
        onClose={() => setPdfPreview(null)}
        pdfUrl={pdfPreview?.url || ""}
        title={pdfPreview?.title || "PDF"}
        backLabel="Back"
      />
    </>
  );
};

export default QuestionResourceActions;
